/**
 * Who else is in the rundown: one avatar per user (initials on their
 * presence colour), a ring when they are editing, and "+N" past `max`.
 * Presentational: the list is `lib/editor/presence.ts`'s `presenceView`.
 */
import type { PresenceUser } from '../../lib/editor/presence.js';
import { cn } from '../../lib/utils.js';

/** Background + text per presence colour index (static strings, so Tailwind sees them). */
export const PRESENCE_CLASSES: readonly string[] = [
  'bg-presence-1 text-presence-1-foreground',
  'bg-presence-2 text-presence-2-foreground',
  'bg-presence-3 text-presence-3-foreground',
  'bg-presence-4 text-presence-4-foreground',
];

export function presenceClass(colour: number): string {
  return PRESENCE_CLASSES[colour % PRESENCE_CLASSES.length] ?? '';
}

export type PresenceAvatarsProps = {
  readonly users: readonly PresenceUser[];
  /** How many avatars before "+N" (default 5). */
  readonly max?: number;
};

export function PresenceAvatars({ users, max }: PresenceAvatarsProps) {
  const limit = max ?? 5;
  const shown = users.slice(0, limit);
  const hidden = users.length - shown.length;
  if (users.length === 0) {
    return (
      <span className="text-xs text-muted-foreground" data-testid="presence-alone">
        Only you
      </span>
    );
  }
  return (
    <ul className="flex items-center -space-x-1.5" aria-label="People in this rundown" data-testid="presence-avatars">
      {shown.map((u) => (
        <li
          key={u.userId}
          title={`${u.displayName} — ${u.activity}`}
          aria-label={`${u.displayName}, ${u.activity}`}
          data-testid="presence-avatar"
          data-activity={u.activity}
          className={cn(
            'flex size-7 items-center justify-center rounded-full border-2 border-card text-xs font-semibold',
            presenceClass(u.colour),
            u.activity === 'editing' && 'ring-2 ring-ring',
          )}
        >
          {u.initials}
        </li>
      ))}
      {hidden > 0 ? (
        <li className="flex size-7 items-center justify-center rounded-full border-2 border-card bg-muted text-xs font-medium text-muted-foreground" data-testid="presence-overflow">
          +{hidden}
        </li>
      ) : null}
    </ul>
  );
}
